/**
 * ラボ 3 の盤面（docs/curriculum/labs.md ラボ 3、components.md §3.2 DictLab）。
 * 辞書 `dict: [String: Int]` と `seen: Set<Int>` を 1 つの盤面に置く。
 */
import type { ApplyResult, LabModel } from './types';
import { swiftOptional, swiftString, vizDict, vizSet } from './swift-format';

export interface DictState {
  /** 入れた順。Swift の辞書に順序は無いので、描くときは並べ替える（labs.md ラボ 3）。 */
  readonly dict: readonly (readonly [string, number])[];
  /** 書いた順。 */
  readonly seen: readonly number[];
}

export type DictOp =
  | { type: 'set'; key: string; value: number }
  | { type: 'get'; key: string }
  | { type: 'increment'; key: string }
  | { type: 'remove'; key: string }
  | { type: 'count' }
  | { type: 'insert'; value: number }
  | { type: 'contains'; value: number };

export const DICT_LABEL = 'dict';
export const SET_LABEL = 'seen';
export const EMPTY_DICT: DictState = { dict: [], seen: [] };

export function lookup(state: DictState, key: string): number | undefined {
  return state.dict.find(([k]) => k === key)?.[1];
}

function put(state: DictState, key: string, value: number): DictState {
  const has = state.dict.some(([k]) => k === key);
  const dict = has
    ? state.dict.map(([k, v]): readonly [string, number] => (k === key ? [k, value] : [k, v]))
    : [...state.dict, [key, value] as const];
  return { dict, seen: state.seen };
}

function apply(state: DictState, op: DictOp): ApplyResult<DictState> {
  switch (op.type) {
    case 'set': {
      const before = lookup(state, op.key);
      return {
        state: put(state, op.key, op.value),
        entry: {
          code: `dict[${swiftString(op.key)}] = ${op.value}`,
          tag: before === undefined ? '入れる' : '上書き',
          changed: [DICT_LABEL],
        },
        moved: 1,
        message:
          before === undefined
            ? `キー「${op.key}」に ${op.value} を入れました。`
            : `キー「${op.key}」の値 ${before} を ${op.value} で上書きしました。`,
      };
    }
    case 'get': {
      const v = lookup(state, op.key);
      const r = swiftOptional(v);
      return {
        state,
        entry: {
          code: `print(dict[${swiftString(op.key)}])`,
          result: r,
          stdout: r,
          tag: '開ける',
          changed: [],
        },
        moved: 0,
        message:
          v === undefined
            ? `キー「${op.key}」はありません。nil です。`
            : `キー「${op.key}」の値は ${v} です（${r}）。`,
      };
    }
    case 'increment': {
      const before = lookup(state, op.key);
      const next = (before ?? 0) + 1;
      return {
        state: put(state, op.key, next),
        entry: {
          code: `dict[${swiftString(op.key)}, default: 0] += 1`,
          tag: '1 増やす',
          changed: [DICT_LABEL],
        },
        moved: 1,
        message:
          before === undefined
            ? `キー「${op.key}」が無かったので 0 から始めて 1 にしました。`
            : `キー「${op.key}」の値を ${before} から ${next} にしました。`,
      };
    }
    case 'remove': {
      const before = lookup(state, op.key);
      return {
        state:
          before === undefined
            ? state
            : { dict: state.dict.filter(([k]) => k !== op.key), seen: state.seen },
        entry: {
          code: `dict[${swiftString(op.key)}] = nil`,
          tag: '消す',
          changed: before === undefined ? [] : [DICT_LABEL],
        },
        moved: before === undefined ? 0 : 1,
        message:
          before === undefined
            ? `キー「${op.key}」は元からありません。何も変わりません。`
            : `キー「${op.key}」を削除しました。`,
      };
    }
    case 'count': {
      const s = String(state.dict.length);
      return {
        state,
        entry: { code: 'print(dict.count)', result: s, stdout: s, tag: '確認', changed: [] },
        moved: 0,
        message: `エントリは ${state.dict.length} 個です。`,
      };
    }
    case 'insert': {
      const has = state.seen.includes(op.value);
      return {
        state: has ? state : { dict: state.dict, seen: [...state.seen, op.value] },
        entry: {
          code: `seen.insert(${op.value})`,
          tag: 'Set',
          changed: has ? [] : [SET_LABEL],
        },
        moved: has ? 0 : 1,
        message: has
          ? `${op.value} は Set にすでにあります。何も変わりません。`
          : `${op.value} を Set に入れました。`,
      };
    }
    case 'contains': {
      const has = state.seen.includes(op.value);
      const s = String(has);
      return {
        state,
        entry: {
          code: `print(seen.contains(${op.value}))`,
          result: s,
          stdout: s,
          tag: '確認',
          changed: [],
        },
        moved: 0,
        message: has
          ? `${op.value} は Set にあります（true）。`
          : `${op.value} は Set にありません（false）。`,
      };
    }
  }
}

export const dictModel: LabModel<DictState, DictOp> = {
  id: 'dict',
  apply,
  declarations: () => ['var dict: [String: Int] = [:]', 'var seen: Set<Int> = []'],
  labels: () => [DICT_LABEL, SET_LABEL],
  vizLine: (_state, label) =>
    label === SET_LABEL ? 'viz.show("seen", seen, as: .set)' : 'viz.dict("dict", dict)',
  snapshot: (state, label) =>
    label === SET_LABEL
      ? { kind: 'set', value: vizSet(state.seen) }
      : { kind: 'dict', value: vizDict(state.dict) },
};

/**
 * エントリを描く順。操作のたびに並べ替え、`print(dict)` の順序が決まっていないことを見た目で伝える
 * （labs.md ラボ 3）。random は [0, 1) を返す関数（テストでは固定する）。
 */
export function shuffledKeys(state: DictState, random: () => number): string[] {
  const keys = state.dict.map(([k]) => k);
  for (let i = keys.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    const a = keys[i];
    const b = keys[j];
    if (a === undefined || b === undefined) continue;
    keys[i] = b;
    keys[j] = a;
  }
  return keys;
}
